"use client";

import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Community } from "@packages/sdk";
import { URL_WEB, useBranch } from "@packages/sdk";
import { DownloadIcon, ShareModal, useModal } from "@packages/ui";
import { useCallback, useRef } from "react";

import { downloadShareCard } from "../../../lib/utils/hallowAdminDashboard/downloadShareCard";
import { CommunityInviteContent } from "./CommunityInviteContent";

export type UseCommunityInviteActionOptions = {
  community: Community | undefined;
  /**
   * Optional callback invoked synchronously before the share modal is shown.
   * Used by the mobile nav to close its full-screen menu overlay first — that
   * overlay renders at `zIndices.modalOverModal` (92), above the `ShareModal`'s
   * `zIndices.modalContent` (91), so without dismissing it the share sheet would
   * mount behind the menu and appear to do nothing. Desktop omits this.
   */
  onBeforeShow?: () => void;
};

/**
 * Opens the community invite `ShareModal` — a QR code (with the community image
 * composited in the centre) encoding a Branch smart link, plus the social share
 * row whose last action downloads the full share asset card as a PNG. Mirrors
 * `useEventShareAction`.
 *
 * The QR encodes a Branch deep link (minted via `useBranch().community`, the
 * same integration the mobile apps and the groups share sheet use) so scanning
 * opens the Hallow app to the community page when installed, falling back to the
 * web community page otherwise. The app deep-link path is singular
 * (`/community/{id}`) while the web fallback is the plural page that actually
 * exists on web (`${URL_WEB}/communities/{id}`). Branch minting is async, so we
 * await the link before opening the modal; if it fails (e.g. missing Branch key
 * or API error) `branch.community` resolves to `$fallback_url`, so the QR still
 * encodes a working web URL.
 */
export const useCommunityInviteAction = ({
  community,
  onBeforeShow,
}: UseCommunityInviteActionOptions) => {
  const t = useTranslations();
  const { track } = useAnalytics();
  const branch = useBranch();
  const shareModal = useModal(ShareModal);
  // Ref to the modal's share asset card; captured for the Download action.
  const cardRef = useRef<HTMLDivElement>(null);

  return useCallback(async () => {
    if (!community) return;

    // Dismiss any containing overlay (e.g. the mobile nav's full-screen menu)
    // before awaiting Branch, so the share modal isn't mounted behind it. The
    // async Branch minting and `shareModal.show()` below dispatch to the global
    // NiceModal store, so they still run correctly after the caller unmounts.
    onBeforeShow?.();

    // Web fallback: the plural community page that actually exists on web, used
    // as the Branch `$fallback_url` and as the QR value if minting fails.
    const webFallbackUrl = `${URL_WEB}/communities/${community.id}`;

    track({
      event: HallowAnalyticsEvent.TappedGroupInvite,
      properties: {
        community_id: community.id,
        location: "admin_dashboard_nav",
      },
    });

    const branchUrl = await branch.community({
      id: community.id,
      name: community.name,
      data: {
        // Plain community link (no user/referral id in the canonical).
        $canonical_identifier: `community-${community.id}`,
        // Fall back to the plural web page on both mobile and desktop.
        $fallback_url: webFallbackUrl,
        $desktop_url: webFallbackUrl,
        $og_title: community.name,
        ...(community.simple_address_label && {
          $og_description: community.simple_address_label,
        }),
        ...(community.image_url && { $og_image_url: community.image_url }),
      },
    });
    const inviteUrl = branchUrl || webFallbackUrl;

    shareModal.show({
      color: community.color_hex ?? "",
      title: community.name,
      description: community.simple_address_label,
      imgSrc: community.image_url ?? "",
      link: inviteUrl,
      contentContainerRef: cardRef,
      showFooterLogo: false,
      children: (
        <CommunityInviteContent community={community} inviteUrl={inviteUrl} />
      ),
      shareButtonsTrailingAction: {
        title: t("community_qr_invite_download"),
        icon: <DownloadIcon.Outline />,
        onClick: () => {
          const node = cardRef.current;
          if (!node) return;
          downloadShareCard({
            node,
            fileName: `${community.name}-invite`,
          }).catch((error) => {
            console.error("Community invite download failed", error);
          });
        },
      },
    });
  }, [branch, community, onBeforeShow, shareModal, t, track]);
};
